import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Chip, CircularProgress } from "@mui/material";
import LanIcon from "@mui/icons-material/Lan";
import StorageIcon from "@mui/icons-material/Storage";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import "./PCAPFileViewer.css";

const GLOBAL_HEADER_LENGTH = 24;
const PACKET_HEADER_LENGTH = 16;
const MAX_DISPLAYED_PACKETS = 500;

const LINKTYPE_LABELS = {
  0: "NULL/Loopback",
  1: "Ethernet",
  101: "Raw IP",
  105: "IEEE 802.11",
  113: "Linux cooked capture",
  127: "Radiotap",
};

const ETHER_TYPE_LABELS = {
  0x0800: "IPv4",
  0x0806: "ARP",
  0x86dd: "IPv6",
  0x8100: "VLAN",
  0x88cc: "LLDP",
};

const PROTOCOL_COLORS = {
  TCP: "primary",
  UDP: "secondary",
  ICMP: "warning",
  ICMPv6: "warning",
  ARP: "success",
  IPv4: "info",
  IPv6: "info",
};

function formatMac(bytes, startOffset) {
  return Array.from(bytes.slice(startOffset, startOffset + 6))
    .map((value) => value.toString(16).padStart(2, "0"))
    .join(":");
}

function formatIPv4(bytes, startOffset) {
  return Array.from(bytes.slice(startOffset, startOffset + 4)).join(".");
}

function formatIPv6(bytes, startOffset) {
  const parts = [];

  for (let index = 0; index < 16; index += 2) {
    const value = (bytes[startOffset + index] << 8) | bytes[startOffset + index + 1];
    parts.push(value.toString(16));
  }

  return parts.join(":");
}

function formatTimestamp(seconds, fraction, isNanosecondPrecision) {
  const millisecondFraction = isNanosecondPrecision
    ? Math.floor(fraction / 1000000)
    : Math.floor(fraction / 1000);
  const remainder = isNanosecondPrecision
    ? fraction.toString().padStart(9, "0")
    : fraction.toString().padStart(6, "0");

  const date = new Date((seconds * 1000) + millisecondFraction);

  if (Number.isNaN(date.getTime())) {
    return `${seconds}.${remainder}`;
  }

  return `${date.toLocaleString()} .${remainder}`;
}

function getLinkTypeLabel(linkType) {
  return LINKTYPE_LABELS[linkType] || `LinkType ${linkType}`;
}

function getEtherTypeLabel(etherType) {
  return ETHER_TYPE_LABELS[etherType] || `0x${etherType.toString(16).padStart(4, "0")}`;
}

function parseTransportDetails(bytes, offset, protocol, isIPv6 = false) {
  if (offset + 4 > bytes.length) {
    return {
      protocol,
      source: isIPv6 ? "IPv6 payload" : "IPv4 payload",
      destination: "",
      info: "Payload truncated",
    };
  }

  if (protocol === 6 || protocol === 17) {
    const sourcePort = (bytes[offset] << 8) | bytes[offset + 1];
    const destinationPort = (bytes[offset + 2] << 8) | bytes[offset + 3];

    return {
      protocol: protocol === 6 ? "TCP" : "UDP",
      sourcePort,
      destinationPort,
      info: `${protocol === 6 ? "TCP" : "UDP"} ${sourcePort} -> ${destinationPort}`,
    };
  }

  if (protocol === 1) {
    return { protocol: "ICMP", info: "ICMP packet" };
  }

  if (protocol === 58) {
    return { protocol: "ICMPv6", info: "ICMPv6 packet" };
  }

  return {
    protocol: isIPv6 ? `IPv6 next header ${protocol}` : `IP protocol ${protocol}`,
    info: "Unparsed transport payload",
  };
}

function parseIPv4Packet(bytes, offset) {
  if (offset + 20 > bytes.length) {
    return {
      protocol: "IPv4",
      source: "Truncated IPv4",
      destination: "",
      info: "IPv4 header truncated",
    };
  }

  const versionAndHeaderLength = bytes[offset];
  const headerLength = (versionAndHeaderLength & 0x0f) * 4;
  const protocolNumber = bytes[offset + 9];
  const source = formatIPv4(bytes, offset + 12);
  const destination = formatIPv4(bytes, offset + 16);
  const transport = parseTransportDetails(bytes, offset + headerLength, protocolNumber);

  return {
    protocol: transport.protocol || "IPv4",
    source: transport.sourcePort != null ? `${source}:${transport.sourcePort}` : source,
    destination: transport.destinationPort != null ? `${destination}:${transport.destinationPort}` : destination,
    info: transport.info,
  };
}

function parseIPv6Packet(bytes, offset) {
  if (offset + 40 > bytes.length) {
    return {
      protocol: "IPv6",
      source: "Truncated IPv6",
      destination: "",
      info: "IPv6 header truncated",
    };
  }

  const nextHeader = bytes[offset + 6];
  const source = formatIPv6(bytes, offset + 8);
  const destination = formatIPv6(bytes, offset + 24);
  const transport = parseTransportDetails(bytes, offset + 40, nextHeader, true);

  return {
    protocol: transport.protocol || "IPv6",
    source: transport.sourcePort != null ? `${source}:${transport.sourcePort}` : source,
    destination: transport.destinationPort != null ? `${destination}:${transport.destinationPort}` : destination,
    info: transport.info,
  };
}

function parseEthernetPacket(bytes) {
  if (bytes.length < 14) {
    return {
      protocol: "Ethernet",
      source: "Truncated Ethernet",
      destination: "",
      info: "Ethernet header truncated",
    };
  }

  const destinationMac = formatMac(bytes, 0);
  const sourceMac = formatMac(bytes, 6);

  let etherTypeOffset = 12;
  let etherType = (bytes[etherTypeOffset] << 8) | bytes[etherTypeOffset + 1];
  let payloadOffset = 14;

  if (etherType === 0x8100 && bytes.length >= 18) {
    etherTypeOffset = 16;
    etherType = (bytes[etherTypeOffset] << 8) | bytes[etherTypeOffset + 1];
    payloadOffset = 18;
  }

  if (etherType === 0x0800) {
    return parseIPv4Packet(bytes, payloadOffset);
  }

  if (etherType === 0x86dd) {
    return parseIPv6Packet(bytes, payloadOffset);
  }

  return {
    protocol: getEtherTypeLabel(etherType),
    source: sourceMac,
    destination: destinationMac,
    info: `EtherType ${getEtherTypeLabel(etherType)}`,
  };
}

function parseRawIpPacket(bytes) {
  if (!bytes.length) {
    return {
      protocol: "Raw IP",
      source: "Unknown",
      destination: "",
      info: "Empty packet payload",
    };
  }

  const version = bytes[0] >> 4;

  if (version === 4) {
    return parseIPv4Packet(bytes, 0);
  }

  if (version === 6) {
    return parseIPv6Packet(bytes, 0);
  }

  return {
    protocol: "Raw IP",
    source: "Unknown",
    destination: "",
    info: `Unsupported IP version ${version}`,
  };
}

function summarizePacket(bytes, linkType) {
  if (linkType === 1) {
    return parseEthernetPacket(bytes);
  }

  if (linkType === 101) {
    return parseRawIpPacket(bytes);
  }

  return {
    protocol: getLinkTypeLabel(linkType),
    source: "Unknown",
    destination: "Unknown",
    info: `Preview supports detailed decoding for Ethernet and raw IP; current link type is ${getLinkTypeLabel(linkType)}`,
  };
}

function parsePcap(arrayBuffer) {
  const view = new DataView(arrayBuffer);

  if (view.byteLength < GLOBAL_HEADER_LENGTH) {
    throw new Error("File is too small to be a valid PCAP capture.");
  }

  const magicBigEndian = view.getUint32(0, false);

  let littleEndian;
  let isNanosecondPrecision = false;

  switch (magicBigEndian) {
    case 0xa1b2c3d4:
      littleEndian = false;
      break;
    case 0xd4c3b2a1:
      littleEndian = true;
      break;
    case 0xa1b23c4d:
      littleEndian = false;
      isNanosecondPrecision = true;
      break;
    case 0x4d3cb2a1:
      littleEndian = true;
      isNanosecondPrecision = true;
      break;
    case 0x0a0d0d0a:
      throw new Error("PCAPNG is not supported yet. This preview currently supports classic .pcap captures.");
    default:
      throw new Error("Unknown capture format. The file does not look like a classic PCAP capture.");
  }

  const versionMajor = view.getUint16(4, littleEndian);
  const versionMinor = view.getUint16(6, littleEndian);
  const thisZone = view.getInt32(8, littleEndian);
  const sigFigs = view.getUint32(12, littleEndian);
  const snapLen = view.getUint32(16, littleEndian);
  const linkType = view.getUint32(20, littleEndian);

  let offset = GLOBAL_HEADER_LENGTH;
  let packetIndex = 0;
  let truncated = false;
  const packets = [];

  while (offset + PACKET_HEADER_LENGTH <= view.byteLength) {
    const seconds = view.getUint32(offset, littleEndian);
    const fraction = view.getUint32(offset + 4, littleEndian);
    const includedLength = view.getUint32(offset + 8, littleEndian);
    const originalLength = view.getUint32(offset + 12, littleEndian);
    const packetStart = offset + PACKET_HEADER_LENGTH;
    const packetEnd = packetStart + includedLength;

    if (packetEnd > view.byteLength) {
      throw new Error(`Packet ${packetIndex + 1} exceeds the file boundary. The capture may be truncated or corrupted.`);
    }

    if (packets.length < MAX_DISPLAYED_PACKETS) {
      const packetBytes = new Uint8Array(arrayBuffer, packetStart, includedLength);
      const packetSummary = summarizePacket(packetBytes, linkType);

      packets.push({
        number: packetIndex + 1,
        timestamp: formatTimestamp(seconds, fraction, isNanosecondPrecision),
        capturedLength: includedLength,
        originalLength,
        protocol: packetSummary.protocol,
        source: packetSummary.source,
        destination: packetSummary.destination,
        info: packetSummary.info,
      });
    } else {
      truncated = true;
    }

    packetIndex += 1;
    offset = packetEnd;
  }

  return {
    metadata: {
      version: `${versionMajor}.${versionMinor}`,
      byteOrder: littleEndian ? "Little-endian" : "Big-endian",
      precision: isNanosecondPrecision ? "Nanosecond" : "Microsecond",
      thisZone,
      sigFigs,
      snapLen,
      linkType,
      linkTypeLabel: getLinkTypeLabel(linkType),
      packetCount: packetIndex,
      displayedPackets: packets.length,
    },
    packets,
    truncated,
  };
}

export default function PCAPFileViewer({ blobUrl }) {
  const [pcapData, setPcapData] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const fetchedUrlRef = useRef(null);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    const loadCapture = async () => {
      if (!blobUrl) {
        setError("No PCAP file URL provided.");
        setIsLoading(false);
        return;
      }

      if (fetchedUrlRef.current === blobUrl || isFetchingRef.current) {
        return;
      }

      try {
        isFetchingRef.current = true;
        setIsLoading(true);
        setError("");

        const response = await fetch(blobUrl);

        if (!response.ok) {
          throw new Error("Failed to fetch PCAP file.");
        }

        const arrayBuffer = await response.arrayBuffer();
        const parsedCapture = parsePcap(arrayBuffer);

        setPcapData(parsedCapture);
        fetchedUrlRef.current = blobUrl;
      } catch (loadError) {
        setPcapData(null);
        setError(loadError.message || "Failed to load PCAP capture.");
      } finally {
        setIsLoading(false);
        isFetchingRef.current = false;
      }
    };

    loadCapture();
  }, [blobUrl]);

  const metadataCards = useMemo(() => {
    if (!pcapData) {
      return [];
    }

    const { metadata } = pcapData;

    return [
      { label: "Format", value: `PCAP v${metadata.version}` },
      { label: "Byte Order", value: metadata.byteOrder },
      { label: "Timestamp Precision", value: metadata.precision },
      { label: "Link Type", value: metadata.linkTypeLabel },
      { label: "Packets", value: metadata.packetCount.toLocaleString() },
      { label: "Snap Length", value: `${metadata.snapLen.toLocaleString()} bytes` },
    ];
  }, [pcapData]);

  const columns = useMemo(() => [
    {
      accessorKey: "number",
      header: "No.",
      size: 60,
      enableColumnFilter: false,
      muiTableBodyCellProps: { sx: { whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" } },
    },
    {
      accessorKey: "timestamp",
      header: "Timestamp",
      size: 210,
      muiTableBodyCellProps: { sx: { fontFamily: "monospace", fontSize: "12px", whiteSpace: "nowrap" } },
    },
    {
      accessorKey: "protocol",
      header: "Protocol",
      size: 110,
      Cell: ({ cell }) => {
        const proto = cell.getValue();
        const color = PROTOCOL_COLORS[proto] || "default";
        return <Chip label={proto} color={color} size="small" variant="outlined" />;
      },
    },
    {
      accessorKey: "source",
      header: "Source",
      size: 200,
      muiTableBodyCellProps: { sx: { fontFamily: "monospace", fontSize: "12px" } },
    },
    {
      accessorKey: "destination",
      header: "Destination",
      size: 200,
      muiTableBodyCellProps: { sx: { fontFamily: "monospace", fontSize: "12px" } },
    },
    {
      accessorKey: "capturedLength",
      header: "Length",
      size: 100,
      enableColumnFilter: false,
      Cell: ({ row }) => `${row.original.capturedLength} / ${row.original.originalLength}`,
      muiTableBodyCellProps: { sx: { whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" } },
    },
    {
      accessorKey: "info",
      header: "Info",
      size: 340,
      muiTableBodyCellProps: { sx: { fontSize: "12px" } },
    },
  ], []);

  const table = useMaterialReactTable({
    columns,
    data: pcapData?.packets ?? [],
    enablePagination: true,
    enableSorting: true,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enableRowSelection: false,
    enableRowActions: false,
    enableColumnOrdering: false,
    enableGrouping: false,
    enableColumnPinning: false,
    positionToolbarAlertBanner: "none",
    initialState: {
      pagination: { pageSize: 100, pageIndex: 0 },
      density: "compact",
    },
    muiTableContainerProps: { sx: { maxHeight: "60vh" } },
    renderTopToolbarCustomActions: () =>
      pcapData?.truncated ? (
        <Alert severity="info" sx={{ py: 0, alignSelf: "center" }}>
          Showing first {pcapData.metadata.displayedPackets.toLocaleString()} of{" "}
          {pcapData.metadata.packetCount.toLocaleString()} packets.
        </Alert>
      ) : null,
  });

  if (isLoading) {
    return (
      <Box className="pcap-viewer-loading">
        <CircularProgress size={24} />
        <span>Loading packet capture...</span>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="warning">{error}</Alert>;
  }

  if (!pcapData) {
    return <Alert severity="info">No packet data available.</Alert>;
  }

  return (
    <div className="pcap-viewer-container">
      <div className="pcap-viewer-header">
        <div className="pcap-viewer-title-row">
          <LanIcon fontSize="small" />
          <span className="pcap-viewer-title">Packet Capture Preview</span>
        </div>
        <Chip icon={<StorageIcon />} label={pcapData.metadata.linkTypeLabel} variant="outlined" />
      </div>

      <div className="pcap-viewer-metadata-grid">
        {metadataCards.map((item) => (
          <div key={item.label} className="pcap-viewer-metadata-card">
            <div className="pcap-viewer-metadata-label">{item.label}</div>
            <div className="pcap-viewer-metadata-value">{item.value}</div>
          </div>
        ))}
      </div>

      <MaterialReactTable table={table} />
    </div>
  );
}